import React, { useMemo } from 'react';
import { X, MapPin, Target, TrendingUp, AlertCircle, Truck, Award, Euro, Timer, Briefcase, ChevronRight } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { formatMoney } from '../../utils/format';
import { localize } from '../../data/i18nData';
import { estimateContractMargin } from '../../utils/contractMargin';
import { estimateContractDailyToll } from '../../utils/contractToll';
import useGameStore from '../../store/gameStore';
import { CLIENT_BY_ID, getLoyaltyTier } from '../../data/clientLoyalty';
import ClientLogo from '../crm/ClientLogo';

const ARG_ICONS = { prix: Euro, délais: Timer, qualité: Award, proximité: MapPin };

/**
 * ContractDetailModal — Phase 3 immersive client brief.
 *
 * Opens when the player clicks a paper note on the cork board. Provides:
 *   - Client header (logo, name, segment, loyalty score)
 *   - Sector context + decision drivers (primary / secondary)
 *   - Simple route map (HQ → destination)
 *   - 3 KPIs : marge estimée, risque (loyalty), valeur stratégique
 *   - Accept / close actions
 */
export function ContractDetailModal({
  contract, vehicles, drivers, marginUnlocked, canAcceptContract,
  onAccept, onClose,
}) {
  const { t } = useTranslation();
  const company = useGameStore(s => s.company);
  const clientLoyalty = useGameStore(s => s.clientLoyalty);

  const c = useMemo(() => contract ? localize('contract', contract) : null, [contract]);
  const client = c?.clientId ? CLIENT_BY_ID[c.clientId] : null;
  const loyaltyData = c?.clientId ? (clientLoyalty?.[c.clientId] || { score: 30 }) : null;
  const tier = loyaltyData ? getLoyaltyTier(loyaltyData.score) : null;
  const margin = c && marginUnlocked ? estimateContractMargin(c, vehicles, drivers) : null;

  if (!c) return null;

  const accepted = canAcceptContract(c);

  // Risque relationnel basé sur le score de loyauté.
  const riskLabel = tier?.id === 'mefiant' ? 'Élevé'
                  : tier?.id === 'neutre' ? 'Modéré'
                  : tier?.id === 'confiant' ? 'Faible'
                  : 'Très faible';
  const riskColor = tier?.id === 'mefiant' ? '#dc2626'
                  : tier?.id === 'neutre' ? '#d97706'
                  : tier?.id === 'confiant' ? '#16a34a'
                  : '#10b981';

  // Valeur stratégique (heuristique) : ponctuel premium + tier élevé.
  const grossRevenue = c.frequency === 'ponctuel' ? (c.revenue || 0) : (c.dailyRevenue || 0) * (c.duration || 1);
  const strategicScore = Math.min(100, Math.round(
    (grossRevenue / 500) // ~50 pts at 25k€ revenue
    + (c.tierMin || 1) * 8 // 8-40 pts based on tier
    + (c.isSpecial ? 20 : 0)
    + (tier?.id === 'fidele' || tier?.id === 'partenaire' ? 10 : 0)
  ));

  const drivers_ = client ? getClientDrivers(client) : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm animate-in fade-in duration-200 p-4"
      onClick={onClose}
      data-testid="contract-detail-modal"
    >
      <div
        className="bg-ink-800 rounded-xl shadow-2xl border border-ink-600 w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with brand-color gradient */}
        <div
          className="px-5 py-4 border-b border-ink-600 relative overflow-hidden"
          style={{ background: client ? `linear-gradient(135deg, ${client.color}33, transparent 70%)` : '' }}
        >
          <div className="flex items-start gap-4 relative z-10">
            {client
              ? <ClientLogo client={client} size="xl" withBorder className="shadow-xl flex-shrink-0" />
              : <ClientLogo category={c.category} size="xl" withBorder className="shadow-xl flex-shrink-0" />
            }
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                {c.isSpecial && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-warn text-ink-900 font-mono font-black uppercase tracking-wider">
                    {t('contracts_special_badge')}
                  </span>
                )}
                <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-ink-700 text-slate-400 font-mono uppercase tracking-wider">T{c.tierMin}+</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-accent/15 text-accent border border-accent/30 font-mono uppercase tracking-wider">
                  {c.frequency === 'ponctuel' ? t('freq_ponctuel') : c.frequency === 'régulier' ? t('freq_regulier') : c.frequency}
                </span>
              </div>
              <h2 className="text-xl font-display font-black text-foreground tracking-tight leading-tight">{c.name}</h2>
              {client && (
                <p className="text-sm text-slate-300 flex items-center gap-1.5 mt-1.5">
                  <Briefcase className="w-3.5 h-3.5 text-slate-500" />
                  {c.clientName}
                  <span className="text-slate-500 mx-1">·</span>
                  <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-ink-700/60 text-slate-300 uppercase tracking-wider">{client.segment}</span>
                  <span className="text-slate-500 mx-1">·</span>
                  <MapPin className="w-3 h-3 text-slate-500" />
                  {client.city}
                </p>
              )}
              {tier && (
                <div className="inline-flex items-center gap-1.5 mt-2 px-2 py-0.5 rounded font-mono text-[10px] font-bold uppercase tracking-wider" style={{ backgroundColor: `${tier.color}22`, color: tier.color, border: `1px solid ${tier.color}55` }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: tier.color }} />
                  {tier.label}
                  <span className="opacity-80">{Math.round(loyaltyData.score)}/100</span>
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-foreground p-1 rounded transition-colors flex-shrink-0"
              data-testid="close-contract-detail"
              aria-label="Fermer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {/* 3 KPIs */}
          <div className="grid grid-cols-3 gap-2" data-testid="contract-kpis">
            <KPI
              icon={TrendingUp}
              label="Marge estimée"
              value={marginUnlocked && margin ? `${formatMoney(margin.margin)}` : '🔒'}
              subValue={marginUnlocked && margin ? `${margin.marginPct}%` : 'TMS + Commercial requis'}
              color={!marginUnlocked ? 'text-slate-500' : (margin && margin.margin < 0 ? 'text-danger' : 'text-money')}
              testid="kpi-margin"
            />
            <KPI
              icon={AlertCircle}
              label="Risque relationnel"
              value={riskLabel}
              subValue={tier ? tier.label : '—'}
              color={`text-[${riskColor}]`}
              valueColor={riskColor}
              testid="kpi-risk"
            />
            <KPI
              icon={Target}
              label="Valeur stratégique"
              value={`${strategicScore}/100`}
              subValue={strategicScore >= 70 ? 'Prioritaire' : strategicScore >= 40 ? 'Standard' : 'Tactique'}
              color="text-accent"
              testid="kpi-strategic"
            />
          </div>

          {/* Brief client + drivers */}
          {client && drivers_ && (
            <section className="bg-ink-900/40 rounded-md border border-ink-700 p-3" data-testid="client-brief">
              <h3 className="text-[10px] uppercase tracking-wider font-bold text-slate-400 flex items-center gap-1.5 mb-2">
                <Briefcase className="w-3 h-3" />
                Brief client
              </h3>
              <p className="text-xs text-slate-300 leading-relaxed mb-3">
                <span className="font-bold text-slate-100">{client.name}</span> est un{client.segment === 'PME' ? 'e' : ''} {SEGMENT_LABEL[client.segment] || 'client'} basé{client.segment === 'PME' ? 'e' : ''} à {client.city}. Son cœur d&apos;activité touche les thèmes : {(client.themes || []).join(', ')}.
              </p>
              <div className="grid grid-cols-2 gap-2">
                {drivers_.primary && (
                  <DriverArgument
                    label="Critère principal"
                    arg={drivers_.primary}
                    weight="majeur"
                    accent="text-accent border-accent/40 bg-accent/10"
                  />
                )}
                {drivers_.secondary && (
                  <DriverArgument
                    label="Critère secondaire"
                    arg={drivers_.secondary}
                    weight="modéré"
                    accent="text-slate-300 border-ink-600 bg-ink-700/50"
                  />
                )}
              </div>
            </section>
          )}

          {/* Pricing summary */}
          <section className="bg-ink-900/40 rounded-md border border-ink-700 p-3" data-testid="pricing-summary">
            <h3 className="text-[10px] uppercase tracking-wider font-bold text-slate-400 flex items-center gap-1.5 mb-2">
              <Euro className="w-3 h-3" />
              Conditions
            </h3>
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Tarif</p>
                <p className="text-base font-mono font-black text-money mt-0.5">
                  {c.frequency === 'ponctuel' ? formatMoney(c.revenue) : `${formatMoney(c.dailyRevenue)}/j`}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Durée</p>
                <p className="text-base font-mono font-black text-foreground mt-0.5">
                  {c.duration ? `${c.duration} j` : '—'}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">CA total</p>
                <p className="text-base font-mono font-black text-money mt-0.5">
                  {formatMoney(grossRevenue)}
                </p>
              </div>
            </div>
            {c.requiresLicense && (
              <p className="text-[10px] mt-2 text-rose-300 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-rose-500/15 border border-rose-500/30 font-mono">
                <Award className="w-3 h-3" /> Permis requis : {c.requiresLicense}
              </p>
            )}
            {/* 12/02/2026 — Péages estimés (retour user prod, non visibles jusqu'ici). */}
            {(c.distanceKm || 0) >= 30 && (
              <div className="mt-2 flex items-center gap-2 text-[10px] text-amber-300" data-testid="contract-toll-hint">
                <MapPin className="w-3 h-3" />
                <span>
                  Péages estimés : <span className="font-mono font-bold">{formatMoney(estimateContractDailyToll(c))}/j</span>
                  <span className="text-slate-500"> · {c.distanceKm} km × 0,08 €/km, plafonné 50 €/j</span>
                </span>
              </div>
            )}
          </section>
        </div>

        {/* Actions */}
        <div className="px-5 py-3 border-t border-ink-600 bg-ink-900/40 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-xs px-3 py-1.5 rounded-md font-mono uppercase tracking-wider bg-ink-700 text-slate-300 hover:bg-ink-600 transition-colors"
            data-testid="contract-detail-close"
          >
            Fermer
          </button>
          <button
            type="button"
            onClick={() => onAccept(c)}
            disabled={!accepted}
            className={`text-xs px-4 py-1.5 rounded-md font-mono font-bold uppercase tracking-wider transition-all inline-flex items-center gap-1.5 ${
              !accepted
                ? 'bg-ink-700 text-slate-500 cursor-not-allowed border border-ink-600'
                : 'bg-accent text-white hover:bg-accent/90 shadow-lg shadow-accent/30'
            }`}
            data-testid="contract-detail-accept"
          >
            {t('accept')}
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

const SEGMENT_LABEL = {
  GC: 'grand compte',
  PME: 'PME',
  PUB: 'collectivité publique',
};

/**
 * getClientDrivers — derives primary/secondary decision drivers from
 * the client's sector. Falls back to the most common drivers if the
 * client's sector is unknown.
 */
function getClientDrivers(client) {
  // Simple heuristic based on segment (full sector matching is in clients.js).
  if (client.segment === 'PUB') return { primary: 'qualité', secondary: 'prix' };
  if (client.segment === 'GC')  return { primary: 'délais', secondary: 'prix' };
  return { primary: 'prix', secondary: 'délais' };
}

function DriverArgument({ label, arg, weight, accent }) {
  const Icon = ARG_ICONS[arg] || Target;
  return (
    <div className={`flex items-center gap-2 px-2.5 py-2 rounded border ${accent}`}>
      <Icon className="w-4 h-4 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-wider font-bold opacity-80">{label}</p>
        <p className="text-xs font-bold capitalize leading-tight">{arg}</p>
        <p className="text-[10px] opacity-70">poids {weight}</p>
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, subValue, color, valueColor, testid }) {
  return (
    <div className="px-3 py-2.5 rounded-md bg-ink-900/40 border border-ink-700" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-slate-500 text-[10px] uppercase tracking-wider font-bold">
        <Icon className="w-3 h-3" />
        {label}
      </div>
      <div className={`text-lg font-mono font-bold mt-1 ${color || 'text-foreground'}`} style={valueColor ? { color: valueColor } : undefined}>
        {value}
      </div>
      {subValue && <div className="text-[10px] text-slate-500 mt-0.5">{subValue}</div>}
    </div>
  );
}

/**
 * Minimal stat card used in the contract detail modal header.
 */
